import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { AbstractControl } from '@angular/forms';
import { FieldType, FieldTypeConfig } from '@ngx-formly/core';
import { Subscription, debounceTime, filter, finalize, startWith } from 'rxjs';
import { IEnvironment } from '../../../models/environment.model';
import {
  ItemSelectionConfig,
  SelectionValidationErrors,
} from '../../../models/irembo-data-table.model';
import {
  updateUrlWithApiGatewayBaseUrl,
  findFormControl,
  getPopulateReferenceForm,
  handleErrorMapping,
} from '../../../../utils/utils/data-fetch-widget-utils';
import { v4 as uuidv4 } from 'uuid';
import { FormStateService } from '../../../services/formly/form-state.service';

@Component({
  standalone: false,
  selector: 'irembogov-custom-item-selection-table',
  templateUrl: './custom-item-selection-table.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomItemSelectionTableComponent
  extends FieldType<FieldTypeConfig>
  implements OnDestroy, OnInit
{
  private readonly environment: IEnvironment;
  private readonly subscriptions = new Subscription();
  private readonly fieldListenerSubscriptions = new Subscription();

  loading = false;
  items: any[] = [];
  targetItems: any[] = [];
  pagination = { page: 0, size: 10, collectionSize: 0 };
  currentPage = 0;
  totalPages = 0;
  searchTerm = '';
  private sourceFieldSubscription?: Subscription;
  private targetFieldSubscription?: Subscription;

  constructor(
    @Inject('environment') private readonly env: IEnvironment,
    private readonly http: HttpClient,
    private readonly cd: ChangeDetectorRef,
    private readonly formStateService: FormStateService
  ) {
    super();
    this.environment = env;
  }

  ngOnInit(): void {
    if (this.field.props.disabled) {
      this.formControl.disable();
    }

    this.initializeTargetItems();
    this.loadDataSource();
    this.cd.detectChanges();
  }

  private initializeTargetItems(): void {
    const config = this.field.props['selectionConfig'] as ItemSelectionConfig;

    if (config.mode === 'add' && config.targetField) {
      // Get reference to target array in form model
      this.targetItems = this.field.model[config.targetField] || [];
      if (!this.field.model[config.targetField]) {
        this.field.model[config.targetField] = this.targetItems;
      }

      // Subscribe to target field changes to react to removals from remove table
      const formRef = getPopulateReferenceForm(this.field, this.form);
      const targetControl = findFormControl(formRef, config.targetField);

      if (targetControl) {
        this.targetFieldSubscription = targetControl.valueChanges
          .pipe(startWith(targetControl.value))
          .subscribe(value => {
            this.targetItems = Array.isArray(value) ? value : [];
            this.cd.detectChanges();
          });
      }
    } else if (config.mode === 'remove') {
      // In remove mode, data comes from the form model directly
      this.targetItems = this.formControl.value || [];
    }
  }

  private loadDataSource(): void {
    const { dataset, data, sourceField, selectionConfig } = this.field.props;
    const config = selectionConfig as ItemSelectionConfig;

    if (this.sourceFieldSubscription) {
      this.sourceFieldSubscription.unsubscribe();
    }

    // Priority 1: API dataset (like dropdown data fetch paginated)
    if (
      dataset &&
      this.field.props['url'] &&
      this.field.props['endpointCode']
    ) {
      // Initialize pagination size from dataset
      this.pagination.size = dataset['pageSize'] || 20;
      const pageStart = dataset['pageStart'] || 0;
      this.currentPage = pageStart;

      // Set UI page to 1 for initial load
      this.pagination.page = 1;

      this.fetchDataFromApi();
      this.setupPayloadFieldListeners();
    }
    // Priority 2: Field reference in form model
    else if (sourceField) {
      const formRef = getPopulateReferenceForm(this.field, this.form);
      const sourceControl = findFormControl(formRef, sourceField);

      if (sourceControl) {
        this.sourceFieldSubscription = sourceControl.valueChanges
          .pipe(startWith(sourceControl.value))
          .subscribe(value => {
            this.items = Array.isArray(value) ? value : [];
            this.cd.detectChanges();
          });
      } else {
        this.items = [];
        this.cd.detectChanges();
      }
    }
    // Priority 3: Direct static data array
    else if (data && Array.isArray(data)) {
      this.items = data;
      this.cd.detectChanges();
    }
    // Priority 4: Remove mode using its own value
    else if (config?.mode === 'remove') {
      this.sourceFieldSubscription = this.formControl.valueChanges
        .pipe(startWith(this.formControl.value))
        .subscribe(value => {
          this.items = Array.isArray(value) ? value : [];
          this.cd.detectChanges();
        });
    }
  }

  private setupPayloadFieldListeners(): void {
    const { payloadKeyMappings, useMultiplePayloadKeys, payloadKey } =
      this.field.props;
    const debounceMs = this.field.props['debounceTime'] || 500;

    if (!this.field.options?.fieldChanges) return;

    const sourceKeys = new Set<string>();

    if (!useMultiplePayloadKeys && payloadKey) {
      sourceKeys.add(payloadKey as string);
    } else if (Array.isArray(payloadKeyMappings)) {
      for (const mapping of payloadKeyMappings) {
        if (!mapping.isStatic && mapping.sourceField) {
          sourceKeys.add(mapping.sourceField as string);
        }
      }
    }

    if (!sourceKeys.size) return;

    this.fieldListenerSubscriptions.add(
      this.field.options.fieldChanges
        .pipe(
          filter(change => sourceKeys.has(change.field?.key as string)),
          debounceTime(debounceMs)
        )
        .subscribe(() => this.resetPaginationAndFetch())
    );
  }

  private resetPaginationAndFetch(): void {
    const dataset = this.field.props['dataset'];
    const pageStart = dataset?.['pageStart'] || 0;
    this.currentPage = pageStart;
    this.pagination.page = 1;
    this.fetchDataFromApi();
  }

  private fetchDataFromApi(): void {
    const { dataset } = this.field.props;
    if (!dataset) return;

    const httpMethod = dataset['httpMethod'] || 'POST';
    let url = this.field.props['url'];

    if (this.field.props['useBaseUrl'] !== false) {
      url = updateUrlWithApiGatewayBaseUrl(url, this.environment);
    }

    const params = { ...dataset['params'] };
    const headers = dataset['headers'] || {};
    const dataField = dataset['dataField'] || '';

    // Set pagination parameters - use current page, not static pageStart
    const pageKey = dataset['pageKey'] || 'page';
    const sizeKey = dataset['sizeKey'] || 'size';
    const pageStart = dataset['pageStart'] || 0;
    params[pageKey] = this.currentPage || pageStart;
    params[sizeKey] = this.pagination.size || dataset['pageSize'] || 20;

    // Add search term if provided
    const searchKey = dataset['searchKey'];
    if (searchKey && this.searchTerm) {
      params[searchKey] = this.searchTerm;
    }

    // Add payload mappings if configured (field listener pattern)
    const payload = this.buildPayload();
    Object.assign(params, payload);

    this.loading = true;
    this.formControl.setErrors(null);

    if (httpMethod === 'POST') {
      this.fetchByPost(url, dataField, params, headers);
    } else {
      this.fetchByGet(url, dataField, params, headers);
    }
  }

  private buildPayload(): Record<string, unknown> {
    const { payloadKeyMappings, payloadKey, useMultiplePayloadKeys } =
      this.field.props;
    const formRef = getPopulateReferenceForm(this.field, this.form);

    if (!useMultiplePayloadKeys && payloadKey) {
      const control = findFormControl(formRef, payloadKey);
      return { [payloadKey]: control?.value };
    }

    const payload: Record<string, unknown> = {};
    if (Array.isArray(payloadKeyMappings)) {
      for (const mapping of payloadKeyMappings) {
        if (mapping.isStatic) {
          payload[mapping.key] = mapping.staticValue;
        } else if (mapping.sourceField) {
          const control = findFormControl(formRef, mapping.sourceField);
          payload[mapping.key] = control?.value;
        }
      }
    }

    return payload;
  }

  private fetchByPost(
    url: string,
    dataField: string,
    params: any,
    headers: any
  ): void {
    const requestParams = {
      endpointCode: this.field.props['endpointCode'],
      callerId: uuidv4(),
      payload: params,
      requester: uuidv4(),
    };

    this.subscriptions.add(
      this.http
        .post<any>(url, requestParams, { headers })
        .pipe(
          finalize(() => {
            this.loading = false;
            this.cd.detectChanges();
          })
        )
        .subscribe({
          next: res => this.handleApiSuccess(res, dataField),
          error: error => this.handleApiError(error),
        })
    );
  }

  private fetchByGet(
    url: string,
    dataField: string,
    params: any,
    headers: any
  ): void {
    const requestParams = {
      endpointCode: this.field.props['endpointCode'],
      callerId: uuidv4(),
      payload: params,
      requester: uuidv4(),
    };

    const queryString = Object.keys(requestParams)
      .map(key => {
        const value = requestParams[key as keyof typeof requestParams];
        return `${key}=${encodeURIComponent(
          typeof value === 'object' ? JSON.stringify(value) : (value as string)
        )}`;
      })
      .join('&');

    this.subscriptions.add(
      this.http
        .get<any>(`${url}?${queryString}`, { headers })
        .pipe(
          finalize(() => {
            this.loading = false;
            this.cd.detectChanges();
          })
        )
        .subscribe({
          next: res => this.handleApiSuccess(res, dataField),
          error: error => this.handleApiError(error),
        })
    );
  }

  private handleApiSuccess(res: any, dataField: string): void {
    try {
      const data = res['data'];
      const responseData = data['response'];
      const responseObject =
        typeof responseData === 'string'
          ? JSON.parse(responseData)
          : responseData;

      this.items = this.extractData(responseObject, dataField);
      this.updatePaginationInfo(responseObject);
      this.formControl.setErrors(null);
    } catch (error) {
      console.error('Error processing response:', error);
      this.items = [];
    }
  }

  private extractData(obj: any, dataField: string): any[] {
    if (!dataField) {
      return Array.isArray(obj) ? obj : [obj];
    }

    const keys = dataField.split('.');
    let value = obj;
    for (const key of keys) {
      value = value?.[key];
    }
    if (Array.isArray(value)) {
      return value;
    }
    return value ?? [];
  }

  private updatePaginationInfo(data: any): void {
    const { dataset } = this.field.props;
    const totalPagesKey = dataset?.['totalPagesKey'] || 'totalPages';
    const currentPageKey = dataset?.['currentPageKey'] || 'currentPage';
    const totalElementsKey = dataset?.['totalElementsKey'];

    this.totalPages = this.getNestedValue(data, totalPagesKey) || 0;
    const apiCurrentPage = this.getNestedValue(data, currentPageKey) || 0;
    this.currentPage = apiCurrentPage;

    // Use totalElements if provided, otherwise calculate from total pages
    if (totalElementsKey) {
      this.pagination.collectionSize =
        this.getNestedValue(data, totalElementsKey) || 0;
    } else {
      this.pagination.collectionSize = this.totalPages * this.pagination.size;
    }
  }

  private getNestedValue(obj: any, path: string): any {
    return path.split('.').reduce((acc, key) => acc?.[key], obj);
  }

  private handleApiError(error: any): void {
    const { errors } = handleErrorMapping(
      error?.error || error,
      this.field.props['errorMapping'],
      'invalidGenericInput',
      this.field,
      this.formControl,
      true
    );

    this.formControl.setErrors(errors);
    this.items = [];
  }

  handleSearch(searchTerm: string): void {
    const { dataset } = this.field.props;

    // Client-side search for static data
    if (!dataset || dataset['searchLocal']) {
      // Handled by base table component
      return;
    }

    // Store search term and reset to first page
    this.searchTerm = searchTerm || '';
    const pageStart = dataset['pageStart'] || 0;
    this.currentPage = pageStart;
    this.pagination.page = 1; // Reset UI to page 1

    // API search - refetch with search parameter
    this.fetchDataFromApi();
  }

  handlePagination(event: any): void {
    // Update page and size from event
    this.pagination.page = event.page;
    this.pagination.size = event.pageSize || this.pagination.size;

    // For API pagination, convert to API's page format (usually 0-indexed)
    if (this.field.props['dataset']) {
      const dataset = this.field.props['dataset'];
      const pageStart = dataset['pageStart'] || 0;

      // Convert from 1-indexed UI page to API page format
      // If API uses 0-indexed: page 1 -> 0, page 2 -> 1, etc.
      // If pageStart is 0, API is 0-indexed; if pageStart is 1, API is 1-indexed
      this.currentPage = pageStart === 0 ? event.page - 1 : event.page;

      this.fetchDataFromApi();
    }
  }

  handleItemAdded(item: any): void {
    const config = this.field.props['selectionConfig'] as ItemSelectionConfig;

    if (config.mode === 'add' && config.targetField) {
      // Find the target control and update it directly to ensure valueChanges emits
      const formRef = getPopulateReferenceForm(this.field, this.form);
      const targetControl = findFormControl(formRef, config.targetField);

      if (targetControl) {
        targetControl.setValue([...this.targetItems]);
      } else {
        // Fallback to model mutation, though it might not trigger listeners
        this.field.model[config.targetField] = [...this.targetItems];
      }

      const onAdd = this.field.props['onAdd'];
      if (onAdd && typeof onAdd === 'function') {
        onAdd(item, this.targetItems);
      }
    }

    this.cd.detectChanges();
  }

  handleItemRemoved(item: any): void {
    const config = this.field.props['selectionConfig'] as ItemSelectionConfig;
    const { sourceField } = this.field.props;

    if (config.mode === 'remove') {
      let controlToUpdate: AbstractControl | null = null;

      // If there's a sourceField, update that control.
      if (sourceField) {
        const formRef = getPopulateReferenceForm(this.field, this.form);
        controlToUpdate = findFormControl(formRef, sourceField);
      } else {
        // Otherwise, update the current field's control.
        controlToUpdate = this.formControl;
      }

      if (controlToUpdate) {
        controlToUpdate.setValue([...this.items]);
      }

      const onRemove = this.field.props['onRemove'];
      if (onRemove && typeof onRemove === 'function') {
        onRemove(item);
      }
    }

    this.cd.detectChanges();
  }

  handleValidationError(errors: SelectionValidationErrors): void {
    if (Object.keys(errors).length > 0) {
      this.formControl.setErrors(errors);
    } else {
      this.formControl.setErrors(null);
    }
    this.cd.detectChanges();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.fieldListenerSubscriptions.unsubscribe();
    if (this.sourceFieldSubscription) {
      this.sourceFieldSubscription.unsubscribe();
    }
    if (this.targetFieldSubscription) {
      this.targetFieldSubscription.unsubscribe();
    }
  }
}
